<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head">
        <h4>الأجهزة المسجلة ({{ devices.total ?? 0 }})</h4>
        <router-link to="/students/device-requests" class="btn btn-outline-primary">
          <i class="pi pi-sync me-1"></i>
          طلبات تغيير الجهاز
        </router-link>
      </div>
      <div class="row mb-3">
        <div class="col-lg-6 col-md-12 col-12">
          <div class="form-group">
            <label for="word">ابحث</label>
            <input type="search" id="word" class="form-control" placeholder="رقم الحجز / اسم الطالب / الهاتف"
                   v-model="word" @keyup="debounceFetch" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>الحالة</label>
            <Dropdown v-model="status" :options="statusOptions" showClear optionLabel="title"
                      placeholder="كل الحالات" @change="fetchDevices(1)" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>حالة التحديث</label>
            <Dropdown v-model="updateStatus" :options="updateStatusOptions" showClear optionLabel="title"
                      placeholder="الكل" @change="fetchDevices(1)" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>وضع الدراسة</label>
            <Dropdown v-model="studyMode" :options="studyModeOptions" showClear optionLabel="title"
                      placeholder="الكل" @change="fetchDevices(1)" />
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>رقم الحجز</th>
            <th>الطالب</th>
            <th>السنة الدراسية</th>
            <th>وضع الدراسة</th>
            <th>المجموعة</th>
            <th>الهاتف</th>
            <th>واتساب</th>
            <th>الماركة</th>
            <th>الشركة المصنعة</th>
            <th>الموديل</th>
            <th>Android ID</th>
            <th>Device UUID</th>
            <th>إصدار أندرويد</th>
            <th>إصدار التطبيق</th>
            <th>حالة التحديث</th>
            <th>المنصة</th>
            <th>آخر دخول</th>
            <th>آخر ظهور</th>
            <th>آخر IP</th>
            <th>الحالة</th>
            <th>التحكم</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in devices.data" :key="d.id">
            <td>{{ d.reservation_number ?? '-' }}</td>
            <td>{{ d.student_name ?? '-' }}</td>
            <td>{{ d.academic_year ?? '-' }}</td>
            <td>
              <span class="status-badge" :class="studyModeBadgeClass(d.study_mode)">{{ studyModeLabel(d.study_mode) }}</span>
            </td>
            <td>{{ d.group ?? '-' }}</td>
            <td>{{ d.phone ?? '-' }}</td>
            <td>{{ d.whatsapp_number ?? '-' }}</td>
            <td>{{ d.brand ?? '-' }}</td>
            <td>{{ d.manufacturer ?? '-' }}</td>
            <td>{{ d.model ?? '-' }}</td>
            <td class="text-truncate" style="max-width: 140px;" :title="d.android_id">{{ d.android_id ?? '-' }}</td>
            <td class="text-truncate" style="max-width: 140px;" :title="d.device_uuid">{{ d.device_uuid ?? '-' }}</td>
            <td>{{ d.android_version ?? '-' }}</td>
            <td>{{ d.app_version ?? '-' }}</td>
            <td class="text-nowrap">
              <div v-if="d.required_version" class="text-muted small">المطلوب: {{ d.required_version }}</div>
              <span v-if="d.needs_update" class="status-badge status-badge--suspended">🔴 يحتاج تحديث</span>
              <span v-else-if="d.required_version_code" class="status-badge status-badge--active">🟢 محدث</span>
              <span v-else>-</span>
            </td>
            <td>{{ d.platform ?? '-' }}</td>
            <td>{{ formatRelativeTime(d.last_login_at) }}</td>
            <td>{{ formatRelativeTime(d.last_seen_at) }}</td>
            <td>{{ d.last_ip ?? '-' }}</td>
            <td>
              <span v-if="d.status === 'suspended'" class="status-badge status-badge--suspended">🔴 معلق</span>
              <span v-else class="status-badge status-badge--active">🟢 نشط</span>
            </td>
            <td class="text-nowrap">
              <router-link class="btn btn-info text-white mx-1" :to="`/students/devices/${d.id}`" tooltip="عرض التفاصيل" flow="up">
                <i class="pi pi-eye"></i>
              </router-link>
              <button v-if="canSuspend && d.status !== 'suspended'" class="btn btn-warning text-white mx-1" type="button"
                      :disabled="busyId === d.id" tooltip="تعليق الجهاز" flow="up" @click="openSuspend(d)">
                <i class="pi pi-spin pi-spinner" v-if="busyId === d.id"></i>
                <i class="pi pi-pause-circle" v-else></i>
              </button>
              <button v-if="canReactivate && d.status === 'suspended'" class="btn btn-success text-white mx-1" type="button"
                      :disabled="busyId === d.id" tooltip="إعادة تفعيل الجهاز" flow="up" @click="reactivate(d)">
                <i class="pi pi-spin pi-spinner" v-if="busyId === d.id"></i>
                <i class="pi pi-play-circle" v-else></i>
              </button>
              <button v-if="canReset" class="btn btn-danger text-white mx-1" type="button"
                      :disabled="busyId === d.id" tooltip="إعادة ضبط الجهاز" flow="up" @click="openReset(d)">
                <i class="pi pi-spin pi-spinner" v-if="busyId === d.id"></i>
                <i class="pi pi-refresh" v-else></i>
              </button>
              <button v-if="canReset" class="btn btn-danger text-white mx-1" type="button"
                      :disabled="busyId === d.id" tooltip="إعادة تعيين الطالب" flow="up" @click="openResetStudent(d)">
                <i class="pi pi-spin pi-spinner" v-if="busyId === d.id"></i>
                <i class="pi pi-replay" v-else></i>
              </button>
              <button v-if="canForceLogout" class="btn btn-dark text-white mx-1" type="button"
                      :disabled="busyId === d.id" tooltip="تسجيل خروج إجباري" flow="up" @click="forceLogout(d)">
                <i class="pi pi-spin pi-spinner" v-if="busyId === d.id"></i>
                <i class="pi pi-sign-out" v-else></i>
              </button>
              <router-link v-if="canRequests" class="btn btn-secondary text-white mx-1"
                           :to="`/students/device-requests?user_id=${d.user_id}`" tooltip="عرض الطلبات" flow="up">
                <i class="pi pi-list"></i>
              </router-link>
            </td>
          </tr>
          <tr v-if="!devices.data || devices.data.length === 0">
            <td colspan="21" class="text-center py-4">لا توجد أجهزة مسجلة</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row" v-if="devices.total">
      <div class="col-12">
        <pagination :data="devices" class="mx-auto d-flex align-items-center justify-content-center pagination"
                    @pagination-change-page="fetchDevices">
          <template v-slot:prev-nav>&lt;</template>
          <template v-slot:next-nav>&gt;</template>
        </pagination>
      </div>
    </div>
  </div>

  <ConfirmDialog group="deviceAction">
    <template #message="slotProps">
      <div class="device-action-dialog">
        <p>{{ slotProps.message.message }}</p>
        <div class="form-group mt-3">
          <label>السبب <span class="text-danger">*</span></label>
          <select class="form-select" v-model="actionReason">
            <option value="" disabled>اختر السبب</option>
            <option v-for="r in slotProps.message.reasons" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
        </div>
        <div class="form-group mt-2" v-if="actionReason === 'other'">
          <label>السبب التفصيلي <span class="text-danger">*</span></label>
          <textarea class="form-control" rows="3" v-model="actionCustomReason"></textarea>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dropdown from 'primevue/dropdown';
import ConfirmDialog from 'primevue/confirmdialog';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { formatRelativeTime } from '@/utils/relativeTime.js';

const SUSPEND_REASONS = [
  { value: 'account_sharing', label: 'مشاركة الحساب' },
  { value: 'lost_phone', label: 'فقدان الهاتف' },
  { value: 'suspicious_activity', label: 'نشاط مشبوه' },
  { value: 'temporary_suspension', label: 'تعليق مؤقت' },
  { value: 'device_under_investigation', label: 'الجهاز قيد التحقيق' },
  { value: 'other', label: 'أخرى' },
];

// users.study_mode ('online'|'offline'|'hybrid'|NULL) — never education_type.
// NULL is a real, distinct state ("غير محدد"), not silently shown as حضوري.
const STUDY_MODE_BADGES = {
  online: { label: 'أونلاين', cls: 'status-badge--online' },
  offline: { label: 'حضوري', cls: 'status-badge--offline' },
  hybrid: { label: 'أونلاين + حضوري', cls: 'status-badge--hybrid' },
};

const RESET_REASONS = [
  { value: 'student_requested_new_phone', label: 'الطالب طلب هاتفاً جديداً' },
  { value: 'phone_replaced', label: 'تم استبدال الهاتف' },
  { value: 'phone_sold', label: 'تم بيع الهاتف' },
  { value: 'factory_reset', label: 'تمت إعادة ضبط المصنع' },
  { value: 'damaged_device', label: 'الجهاز تالف' },
  { value: 'other', label: 'أخرى' },
];

export default {
  name: 'DevicesIndex',
  components: { Breadcrumb, Dropdown, ConfirmDialog, pagination },
  data() {
    return {
      devices: { data: [], total: 0 },
      word: '',
      status: null,
      statusOptions: [
        { id: 'active', title: 'نشط' },
        { id: 'suspended', title: 'معلق' },
      ],
      updateStatus: null,
      updateStatusOptions: [
        { id: 'updated', title: 'محدث' },
        { id: 'needs_update', title: 'يحتاج تحديث' },
      ],
      studyMode: null,
      studyModeOptions: [
        { id: 'online', title: 'أونلاين' },
        { id: 'offline', title: 'حضوري' },
        { id: 'hybrid', title: 'أونلاين + حضوري' },
        { id: 'null', title: 'غير محدد' },
      ],
      searchTimeout: null,
      currentPage: 1,
      busyId: null,
      actionReason: '',
      actionCustomReason: '',
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'الأجهزة المسجلة', path: '/students/devices' },
      ],
    };
  },
  computed: {
    // Device management no longer requires device-specific permissions — any
    // authenticated admin can perform these actions (the backend gates on
    // auth.admin only). These stay as computed flags so the per-row status
    // conditions in the template (e.g. only show Suspend when not suspended)
    // keep working.
    canSuspend() { return true; },
    canReactivate() { return true; },
    canReset() { return true; },
    canForceLogout() { return true; },
    canRequests() { return true; },
  },
  methods: {
    formatRelativeTime,
    debounceFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchDevices(1), 600);
    },
    fetchDevices(page = 1) {
      this.currentPage = page;
      const params = { page };
      if (this.word) params.word = this.word;
      if (this.status) params.status = this.status.id;
      if (this.updateStatus) params.update_status = this.updateStatus.id;
      if (this.studyMode) params.study_mode = this.studyMode.id;

      axios.get('/devices', { params }).then(({ data }) => {
        this.devices = data.data;
      }).catch(() => {
        this.devices = { data: [], total: 0 };
      });
    },
    studyModeLabel(mode) {
      return STUDY_MODE_BADGES[mode]?.label ?? 'غير محدد';
    },
    studyModeBadgeClass(mode) {
      return STUDY_MODE_BADGES[mode]?.cls ?? 'status-badge--blocked';
    },
    openSuspend(device) {
      this.actionReason = '';
      this.actionCustomReason = '';
      this.$confirm.require({
        group: 'deviceAction',
        header: 'تعليق الجهاز',
        message: `سيتم تعليق جهاز الطالب "${device.student_name ?? ''}" مؤقتاً.`,
        reasons: SUSPEND_REASONS,
        acceptLabel: 'تعليق الجهاز',
        rejectLabel: 'رجوع',
        acceptClass: 'p-button-warning',
        accept: () => this.submitSuspend(device),
      });
    },
    submitSuspend(device) {
      if (!this.validateReason()) return;
      this.busyId = device.id;
      axios.post(`/devices/${device.id}/suspend`, {
        reason: this.actionReason,
        custom_reason: this.actionCustomReason || null,
      }).then(({ data }) => {
        if (data.status) {
          Swal.fire('', data.message, 'success');
          Object.assign(device, data.data);
        } else {
          Swal.fire('', data.message, 'error');
        }
      }).catch((err) => {
        Swal.fire('', err.response?.data?.message || 'تعذر تعليق الجهاز', 'error');
      }).finally(() => { this.busyId = null; });
    },
    reactivate(device) {
      Swal.fire({
        title: 'إعادة تفعيل الجهاز؟',
        text: 'سيتمكن الطالب من تسجيل الدخول مرة أخرى من هذا الجهاز.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'تأكيد',
        cancelButtonText: 'رجوع',
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.busyId = device.id;
        axios.post(`/devices/${device.id}/reactivate`).then(({ data }) => {
          if (data.status) {
            Swal.fire('', data.message, 'success');
            Object.assign(device, data.data);
          } else {
            Swal.fire('', data.message, 'error');
          }
        }).catch((err) => {
          Swal.fire('', err.response?.data?.message || 'تعذر إعادة تفعيل الجهاز', 'error');
        }).finally(() => { this.busyId = null; });
      });
    },
    forceLogout(device) {
      Swal.fire({
        title: 'تسجيل خروج إجباري؟',
        text: 'سيتم إنهاء جلسة الجهاز الحالية فوراً دون تعليق أو إعادة ضبط الجهاز. سيحتاج الطالب لتسجيل الدخول مرة أخرى من نفس الجهاز.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'تسجيل خروج إجباري',
        cancelButtonText: 'رجوع',
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.busyId = device.id;
        axios.post(`/devices/${device.id}/force-logout`).then(({ data }) => {
          if (data.status) {
            Swal.fire('', data.message, 'success');
            Object.assign(device, data.data);
          } else {
            Swal.fire('', data.message, 'error');
          }
        }).catch((err) => {
          Swal.fire('', err.response?.data?.message || 'تعذر تسجيل الخروج الإجباري', 'error');
        }).finally(() => { this.busyId = null; });
      });
    },
    openReset(device) {
      this.actionReason = '';
      this.actionCustomReason = '';
      this.$confirm.require({
        group: 'deviceAction',
        header: 'إعادة ضبط الجهاز',
        message: `سيتم حذف الجهاز المسجل للطالب "${device.student_name ?? ''}" نهائياً، وسيتمكن من تسجيل جهاز جديد عند الدخول القادم.`,
        reasons: RESET_REASONS,
        acceptLabel: 'إعادة ضبط الجهاز',
        rejectLabel: 'رجوع',
        acceptClass: 'p-button-danger',
        accept: () => this.submitReset(device),
      });
    },
    submitReset(device) {
      if (!this.validateReason()) return;
      this.busyId = device.id;
      axios.post(`/devices/${device.id}/reset`, {
        reason: this.actionReason,
        custom_reason: this.actionCustomReason || null,
      }).then(({ data }) => {
        if (data.status) {
          Swal.fire('', data.message, 'success');
          this.devices.data = this.devices.data.filter((d) => d.id !== device.id);
        } else {
          Swal.fire('', data.message, 'error');
        }
      }).catch((err) => {
        Swal.fire('', err.response?.data?.message || 'تعذر إعادة ضبط الجهاز', 'error');
      }).finally(() => { this.busyId = null; });
    },
    openResetStudent(device) {
      Swal.fire({
        title: 'إعادة تعيين جهاز الطالب',
        html: 'هل أنت متأكد من إعادة تعيين جهاز هذا الطالب؟<br>سيتم إلغاء ارتباط الجهاز الحالي والجلسة الحالية، وسيتمكن الطالب من تسجيل الدخول من جهاز جديد.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'إعادة التعيين',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.busyId = device.id;
        // Keyed by the STUDENT (user_id), not the device row — the backend wipes
        // every device the student has and invalidates their session.
        axios.post(`/devices/${device.user_id}/reset-binding`).then(({ data }) => {
          if (data.status) {
            Swal.fire('', data.message, 'success');
            // Refresh so all of this student's now-removed devices disappear.
            this.fetchDevices(this.currentPage);
          } else {
            Swal.fire('', data.message, 'error');
          }
        }).catch((err) => {
          Swal.fire('', err.response?.data?.message || 'تعذر إعادة تعيين جهاز الطالب', 'error');
        }).finally(() => { this.busyId = null; });
      });
    },
    validateReason() {
      if (!this.actionReason) {
        Swal.fire('', 'الرجاء اختيار السبب', 'warning');
        return false;
      }
      if (this.actionReason === 'other' && !this.actionCustomReason?.trim()) {
        Swal.fire('', 'الرجاء كتابة السبب التفصيلي', 'warning');
        return false;
      }
      return true;
    },
  },
  mounted() {
    this.fetchDevices();
  },
};
</script>

<style scoped>
.device-action-dialog {
  min-width: 320px;
}
</style>
